/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';
import {
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import debounce from 'lodash/debounce';
import {
  FlatList,
  type StyleProp,
  View,
  type ViewProps,
  type ViewStyle,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  RecentSearchesCarouselView,
  useRecentSearchNavigation,
  useSnapSearchViewFeatureFlag,
} from '@airasia-rn/snap';
// Hydration-safe hook: follows the server's device hint until mounted, so the
// server render and the first client render agree on mobile vs desktop (Phoenix's
// useDeviceInfo reads the window width directly and mismatches on desktop).
import { useDeviceInfo } from '@airasia-rn/ui';
import {
  AskBoFloatingButton,
  HeroBannerView,
  NotificationBannerView,
} from '../../components';
import { useHomepageBlocks } from '../../hooks/useHomepage';
import { useHomepageLoadingRegistry } from '../../hooks/useHomepageLoadingRegistry';
import { useHomepageRecommendations } from '../../hooks/useHomepageRecommendations';
import { useNotificationData } from '../../hooks/useNotificationData';
import { useScrollDirection } from '../../hooks/useScrollDirection';
import {
  isArticleCarouselBlock,
  isBottomNavigationBlock,
  isFooterAdsBannerBlock,
  isHeroBannerBlock,
  isNotificationBannerBlock,
  isPrimeAdsBlock,
  isProductTilesBlock,
  isRecommendationTabsBlock,
} from '../../models/homepageBlockHelpers';
import type {
  HomepageBlock,
  ProductTile,
  ProductTilesData,
} from '../../models/homepageBlockTypes';
import {
  ArticleCarouselView,
  BottomTabWebMobileView,
  SearchWidgetView,
} from '../../widgets';
import FooterAdsBannerView from '../../widgets/FooterAdsBannerView/FooterAdsBannerView';
import ProductTilesView from '../../widgets/ProductTilesView/ProductTilesView';
import { HomeWebAd } from '../../widgets/WebAds';
import HomePageRecommendationSection from './HomePageRecommendationSection';
import {
  useHomePageAnalytics,
  useMovementsEnabled,
  useProfileMovementsAgeEligible,
} from './useHomePageRuntime';
import { styles } from './HomePage.styles';
import type { HomePageProps, HomePageRef } from './HomePage';
import { useHomeNavigation } from '../../contexts/HomeNavigationContext';
import { useDropdownOverflowScroll } from '../../hooks';
import { isChinaDomain } from '../../utils';
import { useFlagEnabled } from '@airasia-rn/account';
import { FEATURE_FLAG_HOME_ENABLE_ADS_RENDER } from '../../constants';

// Web keeps browser-only rendering in this platform file, while shared hooks
// stay in small modules used by both HomePage variants. This preserves the
// React Native platform split without duplicating homepage business logic.
const SEARCH_WIDGET_SOLO_BOTTOM_MARGIN = 200 + 16;

const productTilesForWeb = (data: ProductTilesData): ProductTilesData => ({
  ...data,
  tiles: data.tiles.filter((tile) => !tile.isSearchTab),
});

const isVisible = (
  block: HomepageBlock,
  notificationText?: string,
  isAdsRenderEnabled: boolean = true,
) => {
  if (isProductTilesBlock(block)) return block.data.tiles.length > 0;
  if (isPrimeAdsBlock(block)) return isAdsRenderEnabled;
  if (isFooterAdsBannerBlock(block))
    return isAdsRenderEnabled && block.data.banners.length > 0;
  if (isNotificationBannerBlock(block))
    return Boolean(notificationText?.trim());
  if (isHeroBannerBlock(block)) return block.data.banners.length > 0;
  if (isArticleCarouselBlock(block)) return block.data.sections.length > 0;
  if (isRecommendationTabsBlock(block)) return block.data.tabs.length > 0;
  return false;
};

//This is added so that, the search widget has zIndex as 1, and renders on top of all other components on the homepage
//So that we can render the dropdowns on top of other siblings of search widget
interface CustomCellRendererProps extends ViewProps {
  item: HomepageBlock;
  children: React.ReactNode;
  style: StyleProp<ViewStyle>;
}

const CustomCellRenderer = ({
  item,
  children,
  style,
  ...props
}: CustomCellRendererProps) => {
  const isProductTiles = isProductTilesBlock(item);
  const cellStyle = [style, isProductTiles && { zIndex: 1 }];

  return (
    <View {...props} style={cellStyle}>
      {children}
    </View>
  );
};

const HomePageWeb = forwardRef(function HomePageWeb(
  {
    onBackPress,
    initialSearchWidgetProductId,
    onSearchWidgetTabSelect,
    onAllModulesLoaded,
    onScrollDirectionChange,
    openModule,
    onLoginMissing,
    onMovementsAgeEligibilityChange,
    onAskBotPress,
    onAskBotHover,
    onAskBotTouchStart,
    isMovementsAgeEligible = true,
  }: HomePageProps,
  ref: ForwardedRef<HomePageRef>,
) {
  const { isMobileWidth } = useDeviceInfo();
  const { blocks, isLoading, refreshAll, isSuccess, error, isPlaceholderData } =
    useHomepageBlocks({ enabled: false });
  const { navigateToProductTile } = useHomeNavigation();
  const { refreshAll: refreshRecommendationFeeds } =
    useHomepageRecommendations(blocks);
  // Reads the forced platform slot (see HomepageProvider) so the banner never
  // triggers a second homepage fetch for the detected platform.
  const notificationData = useNotificationData();
  const handleScroll = useScrollDirection(onScrollDirectionChange);
  const movementsAgeEligibility = useProfileMovementsAgeEligible();
  const isMovementsEnabled = useMovementsEnabled(isMovementsAgeEligible);
  useEffect(() => {
    onMovementsAgeEligibilityChange?.(movementsAgeEligibility);
  }, [movementsAgeEligibility, onMovementsAgeEligibilityChange]);
  const {
    snap_recent_searches_enabled: isRecentSearchesEnabled,
    snap_recent_search_max_count: recentSearchCount,
  } = useSnapSearchViewFeatureFlag();
  const isChina = useMemo(() => isChinaDomain(), []);

  const [hasMounted, setHasMounted] = useState(false);
  const [activeSearchWidgetProductId, setActiveSearchWidgetProductId] =
    useState<string | undefined>(initialSearchWidgetProductId);
  const [recommendationTabIndexByUuid, setRecommendationTabIndexByUuid] =
    useState<Record<string, number>>({});
  const flatListRef = useRef<FlatList<HomepageBlock>>(null);
  const onProductTilePress = navigateToProductTile;

  const { enabled: isAdsRenderEnabled } = useFlagEnabled(
    FEATURE_FLAG_HOME_ENABLE_ADS_RENDER,
  );

  useHomePageAnalytics({ error, onLoginMissing, trackScreenload: false });
  useEffect(() => setHasMounted(true), []);
  useEffect(
    () => setActiveSearchWidgetProductId(initialSearchWidgetProductId),
    [initialSearchWidgetProductId],
  );

  const visibleBlocks = useMemo(
    () =>
      blocks.filter((block) =>
        isVisible(block, notificationData?.text, isAdsRenderEnabled),
      ),
    [blocks, notificationData?.text, isAdsRenderEnabled],
  );
  const { handleModuleLoaded } = useHomepageLoadingRegistry({
    blocks: visibleBlocks,
    isWeb: true,
    isLoading,
    isSuccess,
    onAllModulesLoaded,
  });
  const { handleRecentSearchCardPress } = useRecentSearchNavigation({
    navigateToUrl: (url: string) => window.open(url, '_self'),
  });
  const handleRefresh = useCallback(() => {
    setRecommendationTabIndexByUuid({});
    refreshAll(refreshRecommendationFeeds);
  }, [refreshAll, refreshRecommendationFeeds]);
  useImperativeHandle(
    ref,
    () => ({ refresh: handleRefresh, refreshHeader: () => undefined }),
    [handleRefresh],
  );
  const onSearchTabSelected = useCallback(
    (tile: ProductTile) => {
      setActiveSearchWidgetProductId(tile.productId);
      onSearchWidgetTabSelect?.(tile.productId);
    },
    [onSearchWidgetTabSelect],
  );
  const handleProductTileClicked = useMemo(
    () =>
      debounce(
        (redirectionUrl: string, productId: string) =>
          onProductTilePress?.(redirectionUrl, productId),
        500,
        { leading: true, trailing: false },
      ),
    [onProductTilePress],
  );
  const renderRecommendationTabs = useCallback(
    (item: HomepageBlock) => {
      if (!isRecommendationTabsBlock(item)) return null;
      return (
        <View style={styles.blockSpacing}>
          <HomePageRecommendationSection
            block={item}
            mode="content"
            isMovementsEnabled={isMovementsEnabled}
            recommendationTabIndexByUuid={recommendationTabIndexByUuid}
            setRecommendationTabIndexByUuid={setRecommendationTabIndexByUuid}
            openModule={openModule}
          />
        </View>
      );
    },
    [
      isMovementsEnabled,
      openModule,
      recommendationTabIndexByUuid,
      styles.blockSpacing,
    ],
  );

  useDropdownOverflowScroll({
    flatListRef,
    containerIds: ['flights-search-container'],
  });

  // isMobileWidth is stable across SSR and the first client render (server
  // device hint), so the inline search widget can render server-side.
  const shouldShowInlineSearchWidget = !isMobileWidth;
  const isOnlySearchWidgetBlock =
    visibleBlocks.length === 1 &&
    isProductTilesBlock(visibleBlocks[0]) &&
    !isMobileWidth;

  const renderBlock = useCallback(
    ({ item }: { item: HomepageBlock }) => {
      if (isProductTilesBlock(item)) {
        return (
          <View
            style={[
              styles.blockSpacing,
              isOnlySearchWidgetBlock && {
                marginBottom: SEARCH_WIDGET_SOLO_BOTTOM_MARGIN,
              },
            ]}
          >
            {shouldShowInlineSearchWidget && (
              <View style={styles.section} nativeID="flights-search-container">
                <SearchWidgetView
                  searchTabsData={item.data}
                  initialProductId={activeSearchWidgetProductId}
                  onTabSelect={onSearchTabSelected}
                />
              </View>
            )}
            {shouldShowInlineSearchWidget &&
              isRecentSearchesEnabled &&
              activeSearchWidgetProductId === 'snap' && (
                <RecentSearchesCarouselView
                  onCardPress={handleRecentSearchCardPress}
                  maxCount={recentSearchCount}
                />
              )}
            <View style={{ zIndex: -1 }}>
              <ProductTilesView
                productTilesData={
                  shouldShowInlineSearchWidget
                    ? productTilesForWeb(item.data)
                    : item.data
                }
                onProductTileClicked={handleProductTileClicked}
                onDataLoaded={() => handleModuleLoaded(item.uuid)}
              />
            </View>
          </View>
        );
      }
      if (isHeroBannerBlock(item)) {
        return (
          <View style={[styles.blockSpacing, styles.notificationbanner]}>
            <HeroBannerView heroBannerData={item.data} />
          </View>
        );
      }
      if (isPrimeAdsBlock(item)) {
        if (!isAdsRenderEnabled) return null;
        return (
          <HomeWebAd
            adUnit={item.data.primeAd.adUnitId}
            slotId={`home-prime-${item.uuid}`}
            collapseUntilRendered={true}
            bottomSpacing={styles.blockSpacing.marginBottom}
          />
        );
      }
      if (isFooterAdsBannerBlock(item)) {
        if (!isAdsRenderEnabled) return null;
        return (
          <View style={styles.blockSpacing}>
            <FooterAdsBannerView item={item} />
          </View>
        );
      }
      if (isNotificationBannerBlock(item)) {
        return (
          <View style={[styles.blockSpacing, styles.notificationbanner]}>
            <NotificationBannerView
              onDataLoaded={() => handleModuleLoaded(item.uuid)}
            />
          </View>
        );
      }
      if (isArticleCarouselBlock(item)) {
        return (
          <View style={[styles.blockSpacing, styles.notificationbanner]}>
            <ArticleCarouselView articleCarouselData={item.data} />
          </View>
        );
      }
      if (isRecommendationTabsBlock(item))
        return renderRecommendationTabs(item);
      return null;
    },
    [
      activeSearchWidgetProductId,
      handleModuleLoaded,
      handleProductTileClicked,
      handleRecentSearchCardPress,
      isOnlySearchWidgetBlock,
      isRecentSearchesEnabled,
      onSearchTabSelected,
      recentSearchCount,
      renderRecommendationTabs,
      shouldShowInlineSearchWidget,
      styles.blockSpacing,
      styles.notificationbanner,
      styles.section,
    ],
  );

  const bottomNavigationBlock = blocks.find(
    (block) => isBottomNavigationBlock(block) && block.data.items.length > 0,
  );
  const fullscreenSearchWidgetBlock =
    hasMounted && isMobileWidth && activeSearchWidgetProductId
      ? visibleBlocks.find((block) => isProductTilesBlock(block))
      : undefined;
  const homepageListBlocks =
    activeSearchWidgetProductId && !hasMounted ? [] : visibleBlocks;
  const contentContainerStyle = styles.webListContent as StyleProp<ViewStyle>;

  if (
    fullscreenSearchWidgetBlock &&
    isProductTilesBlock(fullscreenSearchWidgetBlock)
  ) {
    return (
      <View style={styles.page}>
        <SafeAreaView edges={['top']} style={styles.container}>
          <View style={styles.searchWidgetFullscreen}>
            <SearchWidgetView
              searchTabsData={fullscreenSearchWidgetBlock.data}
              initialProductId={activeSearchWidgetProductId}
              fullScreen={true}
              onBackPress={onBackPress}
              onTabSelect={onSearchTabSelected}
            />
          </View>
        </SafeAreaView>
      </View>
    );
  }

  return (
    <View style={styles.page}>
      <SafeAreaView edges={['top']} style={styles.container}>
        <FlatList
          ref={flatListRef}
          data={homepageListBlocks}
          renderItem={renderBlock}
          CellRendererComponent={CustomCellRenderer}
          showsVerticalScrollIndicator={false}
          keyExtractor={(item) => item.uuid}
          contentContainerStyle={contentContainerStyle}
          style={[styles.flatList, isPlaceholderData && styles.dimmedContent]}
          onScroll={handleScroll}
          scrollEventThrottle={16}
        />
      </SafeAreaView>
      {onAskBotPress && !isChina && (
        <View pointerEvents="box-none" style={styles.overlay}>
          <AskBoFloatingButton
            onPress={onAskBotPress}
            onHover={onAskBotHover}
            onTouchStart={onAskBotTouchStart}
          />
        </View>
      )}
      {hasMounted &&
        isMobileWidth &&
        bottomNavigationBlock &&
        isBottomNavigationBlock(bottomNavigationBlock) &&
        typeof document !== 'undefined' &&
        createPortal(
          <View style={styles.quickActionsBar}>
            <BottomTabWebMobileView
              data={bottomNavigationBlock.data}
              onActionPress={(_navigationId: string, redirectionUrl: string) =>
                window.open(redirectionUrl, '_blank')
              }
            />
          </View>,
          document.body,
        )}
    </View>
  );
});

export default HomePageWeb;
